import { AssociationModifierListName } from '@deltek/specification-client/core/Models/associationModifierList'
import { ProjectItem } from '@deltek/specification-client/core/Models/projectItem'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { getFullAddress } from '@deltek/specification-client/core/Utilities/stringUtils'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import clsx from 'clsx'
import { format } from 'date-fns'
import React from 'react'
import { availableProjectParamters } from 'shared/utils/projectParameterUtils'
import { getAssociationModifier } from '..'

const useContentStyles = makeStyles((theme: Theme) => ({
    headingText: {
        borderBottom: `3px solid ${theme.palette.common.white}`,
        lineHeight: '24px',
        width: 'fit-content',
        color: theme.palette.common.white,
    },
    infoBlock: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(3),
        width: 370,
        color: theme.palette.common.white,
        wordBreak: 'break-word',
    },
    infoLabel: {
        '&:after': {
            content: '":"',
        },
    },
}))

export interface PopoutInfoContentProps {
    projectItem?: Partial<ProjectItem>
}

const PopoutInfoContent: React.FC<PopoutInfoContentProps> = ({
    projectItem,
}) => {
    const classes = useContentStyles()
    const currentMilestone = projectItem?.currentMilestone
    return (
        <>
            <Typography variant="h5" className={classes.headingText}>
                Project Details
            </Typography>
            <GridContainer direction="column" className={classes.infoBlock}>
                <GridContainer>
                    <GridItem xs={6}>
                        <Typography
                            className={clsx(
                                classes.infoLabel,
                                'projectGroupLabel'
                            )}
                            variant="body2"
                            component="span"
                        >
                            Project Group
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="body2" component="span">
                            {!projectItem?.groupName
                                ? 'Not Set'
                                : projectItem.groupName}
                        </Typography>
                    </GridItem>
                </GridContainer>
                <GridContainer>
                    <GridItem xs={6}>
                        <Typography
                            className={clsx(
                                classes.infoLabel,
                                'projectNumberLabel'
                            )}
                            variant="body2"
                            component="span"
                        >
                            Project Number
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="body2" component="span">
                            {!projectItem?.number
                                ? 'Not Set'
                                : projectItem.number}
                        </Typography>
                    </GridItem>
                </GridContainer>
                <GridContainer>
                    <GridItem xs={6}>
                        <Typography
                            className={clsx(
                                classes.infoLabel,
                                'projectNameLabel'
                            )}
                            variant="body2"
                            component="span"
                        >
                            Project Name
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="body2" component="span">
                            {!projectItem?.name ? 'Not Set' : projectItem.name}
                        </Typography>
                    </GridItem>
                </GridContainer>
                <GridContainer>
                    <GridItem xs={6}>
                        <Typography
                            className={clsx(
                                classes.infoLabel,
                                'constructionBudgetLabel'
                            )}
                            variant="body2"
                            component="span"
                        >
                            Construction Budget
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="body2" component="span">
                            {!projectItem?.budget
                                ? 'Not Set'
                                : projectItem.budget}
                        </Typography>
                    </GridItem>
                </GridContainer>
                <GridContainer>
                    <GridItem xs={6}>
                        <Typography
                            className={clsx(classes.infoLabel, 'addressLabel')}
                            variant="body2"
                            component="span"
                        >
                            Project Location
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="body2" component="span">
                            {!projectItem?.location
                                ? 'Not Set'
                                : getFullAddress(projectItem?.location)}
                        </Typography>
                    </GridItem>
                </GridContainer>
            </GridContainer>
            <Typography variant="h5" className={classes.headingText}>
                Project Parameters
            </Typography>
            <GridContainer direction="column" className={classes.infoBlock}>
                {availableProjectParamters.map((paramter, index) => (
                    <GridContainer key={`${paramter.label}_${index}`}>
                        <GridItem xs={6}>
                            <Typography
                                className={clsx(
                                    classes.infoLabel,
                                    `${paramter.name.replace(' ', '')}Label`
                                )}
                                variant="body2"
                                component="span"
                            >
                                {paramter.label}
                            </Typography>
                        </GridItem>
                        <GridItem xs={6}>
                            <Typography variant="body2" component="span">
                                {getAssociationModifier({
                                    parameters: projectItem?.parameters,
                                    modifierListName: paramter.name as AssociationModifierListName,
                                })?.join(', ') ?? 'Not Set'}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                ))}
            </GridContainer>
            <Typography variant="h5" className={classes.headingText}>
                Project Phase
            </Typography>
            <GridContainer direction="column" className={classes.infoBlock}>
                <GridContainer>
                    <GridItem xs={6}>
                        <Typography
                            className={clsx(
                                classes.infoLabel,
                                'currentPhaseLabel'
                            )}
                            variant="body2"
                            component="span"
                        >
                            Current Phase
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="body2" component="span">
                            {!currentMilestone?.phase?.name
                                ? 'Not Set'
                                : currentMilestone.phase.name}
                        </Typography>
                    </GridItem>
                </GridContainer>
                <GridContainer>
                    <GridItem xs={6}>
                        <Typography
                            className={clsx(
                                classes.infoLabel,
                                'targetCompletionLabel'
                            )}
                            variant="body2"
                            component="span"
                        >
                            Target Completion
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="body2" component="span">
                            {!currentMilestone?.endDate
                                ? 'Not Set'
                                : format(
                                      new Date(currentMilestone.endDate),
                                      'MM/dd/yyyy'
                                  )}
                        </Typography>
                    </GridItem>
                </GridContainer>
            </GridContainer>
        </>
    )
}

export default PopoutInfoContent
